import { useMemo, useRef, useState } from 'preact/hooks';

import { t } from '../i18n';
import { textBlock } from '../story';
import { Icon } from './icons';
import { StoryEditor } from './StoryEditor';

import type { DraftBlock, InsertPoint } from '../story';
import type { FeedbackContext, FeedbackType } from '../types';
import type { CaptureTool } from './StoryEditor';

export interface Draft {
  type: FeedbackType;
  title: string;
  blocks: DraftBlock[];
}

export const emptyDraft = (): Draft => ({ type: 'bug', title: '', blocks: [textBlock()] });

interface Props {
  draft: Draft;
  onChange: (draft: Draft) => void;
  onCapture: (tool: CaptureTool, at: InsertPoint) => void;
  onCancelPicking: () => void;
  onImage: (at: InsertPoint, dataUrl: string) => void;
  onRetry: (blockId: string) => void;
  retryingId: string | null;
  onSubmit: () => Promise<void>;
  focusId: string | null;
  insertAt: InsertPoint | null;
  captureError: string | null;
  context: () => FeedbackContext;
  picking: 'element' | 'area' | 'capturing' | null;
  activeTool: CaptureTool | null;
  draftSaved: boolean;
}

export function NewFeedback(props: Props) {
  const s = t();
  const { draft, onChange } = props;
  const [submitting, setSubmitting] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [summaryMissing, setSummaryMissing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const summaryRef = useRef<HTMLInputElement>(null);
  const context = useMemo(() => props.context(), [showDetails, props.picking]);
  const consoleErrors = context.console.filter((c) => c.level === 'error').length;
  const bug = draft.type === 'bug';

  const send = async () => {
    if (!draft.title.trim()) {
      // Focus moves to the first field with an error; the story and screenshots stay as they are.
      setSummaryMissing(true);
      summaryRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setSendFailed(false);
    try {
      await props.onSubmit();
    } catch {
      setSendFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      class="hfb-form hfb-form-new"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <div
        class="hfb-segmented"
        role="radiogroup"
        aria-label={`${s.reportBug} / ${s.requestFeature}`}
        onKeyDown={(event) => {
          // Arrow keys switch, as in a native radio group.
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          const next = draft.type === 'bug' ? 'feature' : 'bug';
          onChange({ ...draft, type: next });
          const buttons = (event.currentTarget as HTMLElement).querySelectorAll('button');
          buttons[next === 'bug' ? 0 : 1]?.focus();
        }}
      >
        {(['bug', 'feature'] as const).map((type) => (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={draft.type === type}
            tabIndex={draft.type === type ? 0 : -1}
            class={draft.type === type ? 'is-active' : ''}
            onClick={() => onChange({ ...draft, type })}
          >
            <Icon name={type === 'bug' ? 'bug' : 'bulb'} />
            {type === 'bug' ? s.reportBug : s.requestFeature}
          </button>
        ))}
      </div>

      <div class="hfb-field">
        <span class="hfb-label-row">
          <label class="hfb-label" for="hfb-summary">
            {s.summary}
          </label>
          <span class="hfb-hint">{s.required}</span>
        </span>
        <input
          id="hfb-summary"
          ref={summaryRef}
          class={`hfb-input${summaryMissing ? ' is-invalid' : ''}`}
          maxLength={200}
          value={draft.title}
          placeholder={bug ? s.summaryPlaceholderBug : s.summaryPlaceholderFeature}
          aria-invalid={summaryMissing}
          aria-describedby={summaryMissing ? 'hfb-summary-error' : undefined}
          onInput={(e) => {
            if (summaryMissing && e.currentTarget.value.trim()) setSummaryMissing(false);
            onChange({ ...draft, title: e.currentTarget.value });
          }}
        />
        {summaryMissing && (
          <p class="hfb-field-error" id="hfb-summary-error">
            <Icon name="alert" />
            {s.summaryMissing}
          </p>
        )}
      </div>

      <div class="hfb-field">
        <span class="hfb-label-row">
          <span class="hfb-label">{bug ? s.story : s.yourIdea}</span>
          {props.draftSaved && (
            <span class="hfb-hint hfb-saved">
              <Icon name="check" size={14} />
              {s.draftSaved}
            </span>
          )}
        </span>
        <StoryEditor
          blocks={draft.blocks}
          onChange={(blocks) => onChange({ ...draft, blocks })}
          onCapture={props.onCapture}
          onCancelPicking={props.onCancelPicking}
          onImage={props.onImage}
          onRetry={props.onRetry}
          retryingId={props.retryingId}
          focusId={props.focusId}
          insertAt={props.insertAt}
          picking={props.picking}
          activeTool={props.activeTool}
          placeholder={bug ? s.storyPlaceholderBug : s.storyPlaceholderFeature}
        />
        {props.captureError && <p class="hfb-field-error">{props.captureError}</p>}
      </div>

      <section class={`hfb-card hfb-tech${showDetails ? ' is-open' : ''}`}>
        <button
          type="button"
          class="hfb-tech-toggle"
          aria-expanded={showDetails}
          onClick={() => setShowDetails(!showDetails)}
        >
          <Icon name="chevronRight" class="hfb-chevron" />
          <span>
            <strong>{s.techTitle}</strong>
            <span class="hfb-muted">
              {s.techSummary(consoleErrors, context.network.length, context.actions.length)}
            </span>
          </span>
        </button>
        {showDetails && <pre class="hfb-pre">{JSON.stringify(context, null, 2)}</pre>}
      </section>

      <div class="hfb-submit-bar">
        {sendFailed && (
          <div class="hfb-alert" role="alert">
            <Icon name="alert" />
            <span>
              <strong>{s.sendFailedTitle}</strong> {s.sendFailedBody}
            </span>
          </div>
        )}
        <button
          type="submit"
          class="hfb-btn hfb-btn-primary hfb-btn-block hfb-btn-large"
          disabled={submitting}
        >
          {!sendFailed && <Icon name="send" size={18} />}
          {submitting ? s.sending : sendFailed ? s.tryAgain : s.send}
        </button>
      </div>
    </form>
  );
}
